import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSettings, formatNumber } from '../context/SettingsContext';
import { updateRecurringExpense, approveRecurringExpense, searchVehicles } from '../api';
import type { RecurringExpense } from '../api';
import type { ExpenseCategory, VatStatus } from '../types';
import PriceInput from './PriceInput';

interface Props {
  recurring: RecurringExpense;
  approveMode?: boolean;   // true = vis "Godkend" i stedet for "Gem"
  onSaved: () => void;
  onClose: () => void;
}

export default function RecurringExpenseModal({ recurring, approveMode = false, onSaved, onClose }: Props) {
  const { t } = useTranslation();
  const { settings } = useSettings();

  const [date, setDate]               = useState(recurring.next_due_date.slice(0, 10));
  const [category, setCategory]       = useState<ExpenseCategory>((recurring.category as ExpenseCategory) ?? 'other');
  const [description, setDescription] = useState(recurring.description ?? '');
  const [amount, setAmount]           = useState(recurring.amount != null ? formatNumber(recurring.amount, settings.numberFormat) : '');
  const [vatStatus, setVatStatus]     = useState<VatStatus>((recurring.vat_status as VatStatus) ?? 'full');
  const [vatAmount, setVatAmount]     = useState(recurring.vat_amount != null ? formatNumber(recurring.vat_amount, settings.numberFormat) : '');
  const [notes, setNotes]             = useState(recurring.notes ?? '');
  const [frequency, setFrequency]     = useState<'monthly' | 'yearly'>(recurring.frequency);
  const [vehicleId, setVehicleId]     = useState<number | null>(recurring.vehicle_id ?? null);
  const [vehicleLabel, setVehicleLabel] = useState(recurring.registration_number ?? '');
  const [vehicleSearch, setVehicleSearch] = useState('');
  const [vehicleResults, setVehicleResults] = useState<{ id: number; registration_number: string }[]>([]);
  const [saving, setSaving]           = useState(false);

  const parseAmount = (v: string) => parseFloat(v.replace(/\./g, '').replace(',', '.')) || 0;

  const handleVehicleSearch = async (q: string) => {
    setVehicleSearch(q);
    setVehicleLabel('');
    setVehicleId(null);
    if (!q) { setVehicleResults([]); return; }
    const results = await searchVehicles(q);
    setVehicleResults(results);
  };

  const handleSave = async () => {
    setSaving(true);
    const data = {
      vehicle_id: vehicleId,
      category,
      description: description || undefined,
      amount: parseAmount(amount),
      vat_status: vatStatus,
      vat_amount: vatAmount ? parseAmount(vatAmount) : null,
      notes: notes || undefined,
      frequency,
      next_due_date: date,
    };
    if (approveMode) {
      await approveRecurringExpense(recurring.id, data);
    } else {
      await updateRecurringExpense(recurring.id, data);
    }
    setSaving(false);
    onSaved();
  };

  return (
    <div className="modal-overlay">
      <div className="modal-panel max-w-lg">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="modal-title">
            {approveMode ? t('expense.recurring_approve_title') : t('expense.recurring_edit_title')}
          </h2>
          <button onClick={onClose} className="modal-close">&times;</button>
        </div>

        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">{t('events.date')}</label>
              <input type="date" className="input" value={date} onChange={e => setDate(e.target.value)} />
            </div>
            <div>
              <label className="label">{t('expense.category_label')}</label>
              <select className="input" value={category} onChange={e => setCategory(e.target.value as ExpenseCategory)}>
                {(['insurance', 'tax', 'cleaning', 'parking', 'other'] as ExpenseCategory[]).map(c => (
                  <option key={c} value={c}>{t(`expense.category_${c}`)}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Vehicle selector */}
          <div>
            <label className="label">{t('fleet.col_vehicle')}</label>
            <div className="relative">
              <input className="input" placeholder={t('expense.vehicle_placeholder')}
                value={vehicleSearch || vehicleLabel}
                onChange={e => handleVehicleSearch(e.target.value)} />
              {vehicleId && (
                <button type="button"
                  onClick={() => { setVehicleId(null); setVehicleLabel(''); setVehicleSearch(''); }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-xs">
                  {t('expense.scope_fleet')} ✕
                </button>
              )}
              {vehicleResults.length > 0 && (
                <ul className="absolute left-0 top-full z-10 bg-white border rounded shadow-lg w-full text-sm">
                  {vehicleResults.map(v => (
                    <li key={v.id} className="px-3 py-2 hover:bg-[var(--color-primary-soft-bg)] cursor-pointer font-mono"
                      onClick={() => { setVehicleId(v.id); setVehicleLabel(v.registration_number); setVehicleSearch(''); setVehicleResults([]); }}>
                      {v.registration_number}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          <div>
            <label className="label">{t('expense.description')}</label>
            <input className="input" value={description} onChange={e => setDescription(e.target.value)} />
          </div>

          <PriceInput
            label={t('events.cost')}
            amount={amount}
            vatStatus={vatStatus}
            vatAmount={vatAmount}
            onAmountChange={setAmount}
            onVatStatusChange={setVatStatus}
            onVatAmountChange={setVatAmount}
            vatRate={settings.vatRate}
          />

          <div>
            <label className="label">{t('expense.notes')}</label>
            <textarea className="input" rows={2} value={notes} onChange={e => setNotes(e.target.value)} />
          </div>

          <div className="border-t pt-3">
            <label className="label mb-2">{t('expense.frequency_label')}</label>
            <div className="flex gap-4">
              {(['monthly', 'yearly'] as const).map(f => (
                <label key={f} className="flex items-center gap-1.5 cursor-pointer text-sm">
                  <input type="radio" name="rec-frequency" value={f} checked={frequency === f}
                    onChange={() => setFrequency(f)} />
                  {t(`expense.frequency_${f}`)}
                </label>
              ))}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 px-6 pb-6 pt-2">
          <button onClick={onClose} className="btn btn-secondary">{t('modal.close')}</button>
          <button onClick={handleSave} disabled={saving || !amount} className="btn btn-primary">
            {saving ? '...' : approveMode ? t('expense.recurring_approve_btn') : t('modal.save')}
          </button>
        </div>
      </div>
    </div>
  );
}
